import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@somethings-ui/core";
import { CalendarPlus, Check, X } from "lucide-react";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import type { TimeValue } from "react-aria";
import { z } from "zod";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "@/components/ui/popover";
import { NoteReminderForm } from "@/components/conversation-details/notes/note-reminder-form";
import { DEFAULT_TIMEZONE } from "@/lib/constants";
import { useNoteUtils } from "@/lib/hooks/conversations/use-note-utils";

const noteFormSchema = z.object({
  title: z.string().optional(),
  content: z.string().trim().min(1, "Write something first"),
  date: z.date().optional(),
  time: z.any().optional(),
  timezone: z.string().optional(),
});

type NoteFormValues = z.infer<typeof noteFormSchema>;

export interface ContextNoteSubmit {
  title: string;
  content: string;
  reminder: string | null;
  reminderTimezone: string | null;
}

interface ContextNoteEditorProps {
  initialTitle?: string;
  initialContent?: string;
  initialReminder?: string | null;
  initialReminderTimezone?: string | null;
  pending?: boolean;
  onSubmit: (values: ContextNoteSubmit) => void;
  onCancel: () => void;
}

export function ContextNoteEditor({
  initialTitle = "",
  initialContent = "",
  initialReminder = null,
  initialReminderTimezone = null,
  pending = false,
  onSubmit,
  onCancel,
}: ContextNoteEditorProps) {
  const { convertFormValuesToReminder, convertReminderToFormValues } =
    useNoteUtils();

  const initialReminderValues = useMemo(
    () =>
      initialReminder
        ? convertReminderToFormValues(
            initialReminder,
            initialReminderTimezone ?? "",
          )
        : undefined,
    [initialReminder, initialReminderTimezone, convertReminderToFormValues],
  );

  const [reminderOpen, setReminderOpen] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<NoteFormValues>({
    resolver: zodResolver(noteFormSchema),
    defaultValues: {
      title: initialTitle,
      content: initialContent,
      date: initialReminderValues?.date,
      time: initialReminderValues?.time,
      timezone: initialReminderValues?.timezone ?? "",
    },
  });

  const content = watch("content") ?? "";
  const date = watch("date");
  const time = watch("time") as TimeValue | undefined;
  const timezone = watch("timezone") ?? "";
  const canSubmit = content.trim().length > 0 && !pending;

  const reminderIso = convertFormValuesToReminder(date, time, timezone) ?? null;
  const reminderPreview = reminderIso
    ? DateTime.fromISO(reminderIso, {
        zone: timezone || DEFAULT_TIMEZONE,
      }).toFormat("LLL d, h:mm a")
    : null;

  const handleReminderChange = (
    key: string,
    value: Date | TimeValue | string | undefined,
  ) => {
    if (key === "date") setValue("date", value as Date | undefined);
    else if (key === "time") setValue("time", value);
    else if (key === "timezone")
      setValue("timezone", (value as string | undefined) ?? "");
  };

  const clearReminder = () => {
    setValue("date", undefined);
    setValue("time", undefined);
    setValue("timezone", "");
  };

  const submit = handleSubmit((values) => {
    const iso =
      convertFormValuesToReminder(values.date, values.time, values.timezone) ??
      null;
    onSubmit({
      title: values.title?.trim() ?? "",
      content: values.content.trim(),
      reminder: iso,
      reminderTimezone: iso ? values.timezone || DEFAULT_TIMEZONE : null,
    });
  });

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-3 rounded-[16px] border border-surfaces-divider bg-white px-2.5 py-4"
    >
      <input
        {...register("title")}
        placeholder="Title (optional)"
        className="w-full border-b border-[#CBC6BE] bg-transparent pb-1 font-avantt text-[18px] font-bold leading-[22px] text-text-primary outline-none placeholder:text-text-tertiary focus:border-[#4D55FD]"
      />
      <textarea
        {...register("content")}
        rows={3}
        autoFocus
        placeholder="Write what you noticed..."
        className="w-full resize-none border-b border-[#CBC6BE] bg-transparent pb-1 font-body text-body-2 text-text-primary outline-none placeholder:text-text-placeholder focus:border-[#4D55FD]"
      />
      {errors.content ? (
        <p className="font-body text-body-4 text-states-error">
          {errors.content.message}
        </p>
      ) : null}
      <div className="flex items-center justify-between gap-2">
        <Popover open={reminderOpen} onOpenChange={setReminderOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                "flex items-center gap-1 transition-colors hover:text-text-primary",
                reminderPreview ? "text-text-secondary" : "text-text-tertiary",
              )}
            >
              <CalendarPlus className="size-3.5" />
              <span className="font-body text-[11px]">
                {reminderPreview ?? "Add reminder"}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverPortal>
            <PopoverContent align="start" className="w-72">
              <NoteReminderForm
                date={date}
                time={time}
                timezone={timezone}
                setValue={handleReminderChange}
              />
              {reminderIso ? (
                <button
                  type="button"
                  onClick={clearReminder}
                  className="mt-3 font-body text-body-3 text-states-error"
                >
                  Remove reminder
                </button>
              ) : null}
            </PopoverContent>
          </PopoverPortal>
        </Popover>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            aria-label="Cancel"
            className="flex size-7 items-center justify-center rounded-xl text-text-secondary transition-colors hover:bg-surfaces-z0"
          >
            <X className="size-3.5" />
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            aria-label="Save note"
            className={cn(
              "flex size-7 items-center justify-center rounded-xl text-text-primary transition-colors hover:bg-surfaces-z0",
              !canSubmit && "opacity-40",
            )}
          >
            <Check className="size-3.5" />
          </button>
        </div>
      </div>
    </form>
  );
}
